import React from 'react';
import { Box, Paper, Typography, useTheme, alpha } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    label: string;
  };
  subtitle?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, trend, subtitle, onClick }) => {
  const theme = useTheme();
  const isPositive = trend && trend.value >= 0;

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.02)} 100%)`,
        border: `1px solid ${alpha(color, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 32px -8px ${alpha(color, 0.25)}`,
          border: `1px solid ${alpha(color, 0.3)}`,
          cursor: onClick ? 'pointer' : 'default',
          background: `linear-gradient(135deg, ${alpha(color, 0.12)} 0%, ${alpha(color, 0.04)} 100%)`,
        },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '140px',
          height: '140px',
          background: `radial-gradient(circle at top right, ${alpha(color, 0.1)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2.5,
            bgcolor: alpha(color, 0.15),
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 12px ${alpha(color, 0.15)}`,
          }}
        >
          {icon}
        </Box>
        {trend && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: isPositive ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
              color: isPositive ? theme.palette.success.main : theme.palette.error.main,
              borderRadius: 20,
              px: 1,
              py: 0.5,
            }}
          >
            {isPositive ? <TrendingUp fontSize="small" sx={{ mr: 0.5 }} /> : <TrendingDown fontSize="small" sx={{ mr: 0.5 }} />}
            <Typography variant="caption" fontWeight="bold">
              {Math.abs(trend.value)}%
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography variant="h4" fontWeight="800" sx={{ mb: 0.5, color: theme.palette.text.primary }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight="600">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default StatCard;
